import {
  Alert,
  Autocomplete,
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "../Buttons/LoadingButton";
import { Question } from "src/api/types";
import { useCallback } from "react";
import { useGetQuestionsQuery } from "src/api/services/questionsApi";

interface Props {
  position: number;
  setPosition: (v: number) => void;
  selectedQuestion: Question | null;
  setSelectedQuestion: (v: Question | null) => void;
  nextQuestion: Question | null;
  setNextQuestion: (v: Question | null) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
  isLoading: boolean;
  setError: (v: string) => void;
  error: string;
}

const QuestionForm = ({
  isLoading,
  nextQuestion,
  onSubmit,
  position,
  selectedQuestion,
  setNextQuestion,
  setPosition,
  setSelectedQuestion,
  error,
  setError,
}: Props) => {
  const { questions } = useGetQuestionsQuery(
    { from: 0, to: 1000 },
    {
      selectFromResult: ({ data }) => {
        return { questions: data?.data ?? [] };
      },
    },
  );

  const handlePosition = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPosition(Number(event.target.value));
      setError("");
    },
    [setError, setPosition],
  );

  const handleQuestion = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Question | null) => {
      setSelectedQuestion(value);
      setError("");
    },
    [setError, setSelectedQuestion],
  );

  const handleNextQuestion = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Question | null) => {
      setNextQuestion(value);
      setError("");
    },
    [setError, setNextQuestion],
  );

  return (
    <Stack
      gap={2}
      component="form"
      onSubmit={onSubmit}
      aria-label="question-form"
    >
      <Autocomplete
        options={questions}
        getOptionLabel={(p) => `${p.id}: ${p.title}`}
        value={selectedQuestion}
        onChange={handleQuestion}
        renderInput={(params) => (
          <TextField {...params} label="Question" required />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box key={key} {...optionProps} component="li">
              <Stack>
                <Typography textAlign="left">{option.title}</Typography>
                {option.subtitle ? (
                  <Typography
                    textAlign="left"
                    variant="caption"
                    color="text.secondary"
                  >
                    {option.subtitle}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        }}
      />
      <TextField
        label="Position"
        type="number"
        required
        value={position ?? ""}
        onChange={handlePosition}
      />
      <Autocomplete
        options={questions}
        getOptionLabel={(p) => `${p.id}: ${p.title}`}
        value={nextQuestion}
        onChange={handleNextQuestion}
        renderInput={(params) => (
          <TextField {...params} label="Next question" />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box key={key} {...optionProps} component="li">
              <Stack>
                <Typography textAlign="left">{option.title}</Typography>
                {option.subtitle ? (
                  <Typography
                    textAlign="left"
                    variant="caption"
                    color="text.secondary"
                  >
                    {option.subtitle}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        }}
      />
      <LoadingButton type="submit" loading={isLoading}>
        Create question
      </LoadingButton>
      {error ? <Alert severity={"error"}>{error}</Alert> : null}
    </Stack>
  );
};

export default QuestionForm;
